import React, { useState, useEffect } from "react";
import { X, ChevronRight, AlertTriangle, Upload } from "lucide-react";
import { BASE_URL } from "utils/constant";
import avatar from "../assets/3d_avatar_26.png";
const AccountModal = ({ isOpen, onClose, userData, handleLogOut, refetch }) => {
  // console.log("account info", userData);
  const [activeTab, setActiveTab] = useState("profile");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: userData.first_name,
    last_name: userData.last_name,
    contact_number: userData.contact_number,
    profession: userData.profession,
  });
  const [addressData, setAddressData] = useState({
    street_address: userData.address,
    city: userData.city,
    state: userData.state,
    pincode: userData.pincode,
    country: "India", // Fixed as India
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(
    userData.userpic || avatar
  );
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: choose method, 2: OTP, 3: verify OTP, 4: new password
  const [resetMethod, setResetMethod] = useState(""); // 'otp' or 'current'
  const [otpValue, setOtpValue] = useState("");
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isCustomProfession, setIsCustomProfession] = useState(false);
  const [customProfessionValue, setCustomProfessionValue] = useState("");
  const [hasAddress, setHasAddress] = useState(false);

  // Profession options
  const professionOptions = [
    "Contractor",
    "Supervisor",
    "Manager",
    "Architect",
    "Engineer",
    "Vendor",
    "Employee",
    "Others",
    "Custom", // Add this option
  ];

  const isCurrentProfessionInList = professionOptions
    .slice(0, -1) // Exclude "Custom" from check
    .map((opt) => opt.toLowerCase())
    .includes(userData.profession?.toLowerCase());

  useEffect(() => {
    if (
      userData.profession &&
      !isCurrentProfessionInList &&
      userData.profession !== "owner"
    ) {
      setIsCustomProfession(true);
      setCustomProfessionValue(userData.profession);
      setProfileData((prev) => ({ ...prev, profession: "custom" }));
    }
  }, [userData.profession, isCurrentProfessionInList]);
  // Fetch address data on mount
  useEffect(() => {
    if (isOpen && userData?.id) {
      fetchAddressData();
    }
  }, [isOpen, userData?.id]);

  const fetchAddressData = async () => {
    try {
      setIsLoading(true);
      const access = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/auth/get-address/${userData?.address_id}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch address data");
      }

      const result = await response.json();

      if (result.status && result.data) {
        setAddressData({
          street_address: result.data.street_address || "",
          city: result.data.city || "",
          state: result.data.state || "",
          pincode: result.data.pincode || "",
          country: "India",
        });
        setHasAddress(true);
        setMessage({ text: "", type: "" }); // Clear any previous error messages
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      setHasAddress(false);
      setMessage({ text: "No address found. Please create your address below.", type: "info" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressData({ ...addressData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateProfile = async () => {
    try {
      setIsLoading(true);
      setMessage({ text: "", type: "" });
      const access = localStorage.getItem("access");
      // If user is owner, don't update profession
      const updateData = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        contact_number: profileData.contact_number,
      };

      if (userData?.profession !== "owner") {
        updateData.profession = isCustomProfession
          ? customProfessionValue
          : profileData.profession;
      }
      // If there's a profile image to update
      let response;

      if (profileImage) {
        const formData = new FormData();
        Object.keys(updateData).forEach((key) => {
          formData.append(key, updateData[key]);
        });
        formData.append("userpic", profileImage);

        response = await fetch(
          `${BASE_URL}/auth/update-userdetails/${userData.id}/`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${access}`,
              //   "Content-Type": "multipart/form-data", // No need to set this header, as the browser will set it automatically for FormData
            },
            body: formData,
          }
        );
      } else {
        response = await fetch(
          `${BASE_URL}/auth/update-userdetails/${userData.id}/`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${access}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updateData),
          }
        );
      }

      const result = await response.json();

      if (response.ok) {
        setMessage({ text: "Profile updated successfully", type: "success" });
        refetch();
      } else {
        setMessage({
          text: result.message || "Failed to update profile",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage({ text: "Failed to update profile", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const updateAddress = async () => {
    try {
      setIsLoading(true);
      setMessage({ text: "", type: "" });
      const access = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/auth/update-address/${userData?.address_id}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${access}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            street_address: addressData.street_address,
            city: addressData.city,
            state: addressData.state,
            pincode: addressData.pincode,
            country: "India",
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setMessage({ text: "Address updated successfully", type: "success" });
        refetch();
      } else {
        setMessage({
          text: result.message || "Failed to update address",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error updating address:", error);
      setMessage({ text: "Failed to update address", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const createAddress = async () => {
    try {
      setIsLoading(true);
      setMessage({ text: "", type: "" });
      const access = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/auth/create-address/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${access}`,
        },
        body: JSON.stringify({
          street_address: addressData.street_address,
          city: addressData.city,
          state: addressData.state,
          pincode: addressData.pincode,
          country: "India",
        }),
      });

      const result = await response.json();

      if (result.status) {
        setMessage({ text: "Address created successfully", type: "success" });
        setHasAddress(true);
        refetch(); // Refresh user data to get the new address_id
        // After a short delay, fetch the address data to populate the form
        setTimeout(() => {
          fetchAddressData();
        }, 1000);
      } else {
        setMessage({
          text: result.message || "Failed to create address",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error creating address:", error);
      setMessage({ text: "Failed to create address", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccount = async () => {
    try {
      setIsLoading(true);
      setMessage({ text: "", type: "" });
      const access = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/auth/delete-user/${userData?.user}/`,
        {
          headers: {
            Authorization: `Bearer ${access}`,
            "Content-Type": "application/json",
          },
          method: "DELETE",
        }
      );

      if (response.ok) {
        setMessage({ text: "Account deleted successfully", type: "success" });
        // Close modal and logout
        setTimeout(() => {
          onClose();
          handleLogOut();
        }, 1500);
      } else {
        const result = await response.json();
        setMessage({
          text: result.message || "Failed to delete account",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      setMessage({ text: "Failed to delete account", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const changePasswordWithCurrent = async () => {
    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setMessage({ text: "New passwords don't match", type: "error" });
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setMessage({
          text: "Password must be at least 6 characters long",
          type: "error",
        });
        return;
      }

      setIsLoading(true);
      setMessage({ text: "", type: "" });
      const access = localStorage.getItem("access");

      const response = await fetch(`${BASE_URL}/auth/change-password/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${access}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage({ text: "Password changed successfully", type: "success" });
        setResetPasswordModal(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        // Optionally logout user to re-login with new password
        setTimeout(() => {
          handleLogOut();
        }, 1500);
      } else {
        setMessage({
          text: result.message || "Failed to change password",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setMessage({ text: "Failed to change password", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Send OTP for password reset
  const sendResetOTP = async () => {
    try {
      setIsLoading(true);
      setMessage({ text: "", type: "" });

      const response = await fetch(`${BASE_URL}/auth/resendotp/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userData.user_email,
        }),
      });

      const result = await response.json();

      if (result.status) {
        setMessage({ text: "OTP sent to your email", type: "success" });
        setResetStep(3); // Move to OTP verification step
      } else {
        setMessage({
          text: result.message || "Failed to send OTP",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setMessage({ text: "Failed to send OTP", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP
  const verifyResetOTP = async () => {
    try {
      if (!otpValue || otpValue.length < 4) {
        setMessage({ text: "Please enter a valid OTP", type: "error" });
        return;
      }

      setIsLoading(true);
      setMessage({ text: "", type: "" });

      const response = await fetch(`${BASE_URL}/auth/otp-verify-password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userData.user_email,
          otp: otpValue,
        }),
      });

      const result = await response.json();

      if (result?.status) {
        setMessage({ text: "OTP verified successfully", type: "success" });
        setResetStep(4); // Move to new password step
      } else {
        setMessage({
          text: result.message || "Invalid OTP",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setMessage({ text: "Failed to verify OTP", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password with OTP
  const resetPasswordWithOTP = async () => {
    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setMessage({ text: "Passwords don't match", type: "error" });
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setMessage({
          text: "Password must be at least 6 characters long",
          type: "error",
        });
        return;
      }

      setIsLoading(true);
      setMessage({ text: "", type: "" });

      const response = await fetch(`${BASE_URL}/auth/reset-password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify({
          email: userData.user_email,
          password: passwordData.newPassword,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage({ text: "Password reset successfully", type: "success" });
        setResetPasswordModal(false);
        // Reset all states
        setResetStep(1);
        setResetMethod("");
        setOtpValue("");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        // Logout user to re-login with new password
        setTimeout(() => {
          handleLogOut();
        }, 1500);
      } else {
        setMessage({
          text: result.message || "Failed to reset password",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setMessage({ text: "Failed to reset password", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password input changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  // Reset modal states when closing
  const closeResetModal = () => {
    setResetPasswordModal(false);
    setResetStep(1);
    setResetMethod("");
    setOtpValue("");
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setMessage({ text: "", type: "" });
  };

  const handleProfessionChange = (e) => {
    const { value } = e.target;

    if (value === "custom") {
      setIsCustomProfession(true);
      setCustomProfessionValue("");
      setProfileData({ ...profileData, profession: "custom" });
    } else {
      setIsCustomProfession(false);
      setCustomProfessionValue("");
      setProfileData({ ...profileData, profession: value });
    }
  };

  const handleCustomProfessionChange = (e) => {
    setCustomProfessionValue(e.target.value);
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-scroll"
        style={{ scrollbarWidth: "none" }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Account Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* User Profile Info */}
        <div className="flex items-center space-x-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="relative">
            <img
              src={profileImagePreview}
              alt="User"
              className="h-16 w-16 rounded-full object-cover ring-2 ring-[#3B0069]"
            />
            {activeTab === "profile" && (
              <label
                htmlFor="profile-upload"
                className="absolute -bottom-1 -right-1 bg-[#3B0069] rounded-full p-1 cursor-pointer hover:bg-opacity-90"
              >
                <Upload className="h-3 w-3 text-white" />
                <input
                  type="file"
                  id="profile-upload"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            )}
          </div>
          <div>
            <h3 className="font-medium text-gray-800">
              {userData?.first_name} {userData?.last_name}
            </h3>
            <p className="text-sm text-gray-500">{userData?.user_email}</p>
            <p className="text-xs text-gray-400 mt-1">{userData?.profession}</p>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            className={`py-2 px-4 ${
              activeTab === "profile"
                ? "text-[#3B0069] border-b-2 border-[#3B0069]"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("profile")}
          >
            Profile
          </button>
          <button
            className={`py-2 px-4 ${
              activeTab === "address"
                ? "text-[#3B0069] border-b-2 border-[#3B0069]"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("address")}
          >
            Address
          </button>
          <button
            className={`py-2 px-4 ${
              activeTab === "security"
                ? "text-[#3B0069] border-b-2 border-[#3B0069]"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("security")}
          >
            Security
          </button>
        </div>
        {/* Status messages */}
        {message.text && (
          <div
            className={`py-2 px-3 rounded mb-4 text-sm ${
              message.type === "success"
                ? "bg-green-50 text-green-600"
                : message.type === "info"
                ? "bg-blue-50 text-blue-600"
                : "bg-red-50 text-red-600"
            }`}
          >
            {message.text}
          </div>
        )}
        {/* Profile Tab Content */}
        {activeTab === "profile" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={profileData.first_name}
                  onChange={handleProfileChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={profileData.last_name}
                  onChange={handleProfileChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number
              </label>
              <input
                type="tel"
                name="contact_number"
                value={profileData.contact_number}
                onChange={handleProfileChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profession
                {userData?.profession === "owner" && (
                  <span className="text-xs text-gray-500 ml-2">
                    (Cannot be changed)
                  </span>
                )}
              </label>
              <select
                name="profession"
                value={profileData.profession}
                onChange={handleProfessionChange}
                disabled={userData?.profession === "owner"}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069] disabled:bg-gray-100"
              >
                {/* Show current profession if it's not in the list */}
                {!isCurrentProfessionInList &&
                  userData.profession !== "owner" && (
                    <option value={userData.profession}>
                      {userData.profession.charAt(0).toUpperCase() +
                        userData.profession.slice(1)}
                    </option>
                  )}

                {professionOptions.map((option) => (
                  <option key={option} value={option.toLowerCase()}>
                    {option}
                  </option>
                ))}

                <option
                  value="owner"
                  disabled={userData?.profession !== "owner"}
                >
                  Owner
                </option>
              </select>

              {/* Custom profession input field */}
              {isCustomProfession && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={customProfessionValue}
                    onChange={handleCustomProfessionChange}
                    placeholder="Enter your profession"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                  />
                </div>
              )}
            </div>

            <button
              onClick={updateProfile}
              disabled={isLoading}
              className="w-full py-2 bg-[#3B0069] text-white rounded-lg font-medium hover:bg-opacity-90 transition-all disabled:bg-opacity-70"
            >
              {isLoading ? "Updating..." : "Update Profile"}
            </button>
          </div>
        )}
        {/* Address Tab Content */}
        {activeTab === "address" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              <input
                type="text"
                name="street_address"
                value={addressData.street_address}
                onChange={handleAddressChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={addressData.city}
                  onChange={handleAddressChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={addressData.state}
                  onChange={handleAddressChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PIN Code
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={addressData.pincode}
                  onChange={handleAddressChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value="India"
                  disabled
                  className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                />
              </div>
            </div>

            <button
              onClick={hasAddress ? updateAddress : createAddress}
              disabled={isLoading}
              className="w-full py-2 bg-[#3B0069] text-white rounded-lg font-medium hover:bg-opacity-90 transition-all disabled:bg-opacity-70"
            >
              {isLoading 
                ? (hasAddress ? "Updating..." : "Creating...") 
                : (hasAddress ? "Update Address" : "Create Address")
              }
            </button>
          </div>
        )}
        {/* Security Tab Content */}
        {activeTab === "security" && (
          <div className="space-y-4">
            <button
              className="w-full flex items-center justify-between p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-700"
              onClick={() => setResetPasswordModal(true)}
            >
              <span>Reset Password</span>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>

            <button
              className="w-full flex items-center justify-between p-3 text-left bg-red-50 hover:bg-red-100 rounded-lg text-red-600"
              onClick={() => setIsDeleteConfirmOpen(true)}
            >
              <span>Delete Account</span>
              <AlertTriangle className="h-4 w-4" />
            </button>

            <button
              className="w-full mt-4 py-3 bg-[#3B0069] text-white rounded-lg font-medium hover:bg-opacity-90 transition-all"
              onClick={handleLogOut}
            >
              Logout
            </button>
          </div>
        )}
        {/* Delete Account Confirmation */}
        {isDeleteConfirmOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-red-600 mb-2">
                Confirm Account Deletion
              </h3>
              <p className="text-gray-700 mb-4">
                This action cannot be undone. All your data will be permanently
                deleted.
              </p>

              <div className="flex space-x-3 justify-end">
                <button
                  onClick={() => setIsDeleteConfirmOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteAccount}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-opacity-70"
                >
                  {isLoading ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            </div>
          </div>
        )}
        {resetPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Reset Password
                </h3>
                <button
                  onClick={closeResetModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Step 1: Choose Reset Method */}
              {resetStep === 1 && (
                <div className="space-y-4">
                  <p className="text-gray-600 mb-4">
                    Choose how you'd like to reset your password:
                  </p>

                  <button
                    onClick={() => {
                      setResetMethod("current");
                      setResetStep(2);
                    }}
                    className="w-full p-3 text-left border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <div className="font-medium text-gray-800">
                      Use Current Password
                    </div>
                    <div className="text-sm text-gray-500">
                      Change password with your current password
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setResetMethod("otp");
                      setResetStep(2);
                    }}
                    className="w-full p-3 text-left border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <div className="font-medium text-gray-800">
                      Use Email OTP
                    </div>
                    <div className="text-sm text-gray-500">
                      Reset via OTP sent to {userData.user_email}
                    </div>
                  </button>
                </div>
              )}

              {/* Step 2: Current Password Method */}
              {resetStep === 2 && resetMethod === "current" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                      placeholder="Enter current password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                      placeholder="Enter new password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                      placeholder="Confirm new password"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setResetStep(1)}
                      className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={changePasswordWithCurrent}
                      disabled={
                        isLoading ||
                        !passwordData.currentPassword ||
                        !passwordData.newPassword ||
                        !passwordData.confirmPassword
                      }
                      className="flex-1 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-opacity-90 disabled:bg-opacity-70"
                    >
                      {isLoading ? "Changing..." : "Change Password"}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: OTP Method - Send OTP */}
              {resetStep === 2 && resetMethod === "otp" && (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    We'll send an OTP to your email address:
                  </p>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-800">
                      {userData.user_email}
                    </span>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setResetStep(1)}
                      className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={sendResetOTP}
                      disabled={isLoading}
                      className="flex-1 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-opacity-90 disabled:bg-opacity-70"
                    >
                      {isLoading ? "Sending..." : "Send OTP"}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Verify OTP */}
              {resetStep === 3 && resetMethod === "otp" && (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Enter the OTP sent to your email:
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OTP
                    </label>
                    <input
                      type="text"
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069] text-center text-lg tracking-widest"
                      placeholder="Enter OTP"
                      maxLength={6}
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setResetStep(2)}
                      className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={verifyResetOTP}
                      disabled={isLoading || !otpValue}
                      className="flex-1 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-opacity-90 disabled:bg-opacity-70"
                    >
                      {isLoading ? "Verifying..." : "Verify OTP"}
                    </button>
                  </div>

                  <button
                    onClick={sendResetOTP}
                    disabled={isLoading}
                    className="w-full py-2 text-sm text-[#3B0069] hover:underline"
                  >
                    Resend OTP
                  </button>
                </div>
              )}

              {/* Step 4: Set New Password */}
              {resetStep === 4 && resetMethod === "otp" && (
                <div className="space-y-4">
                  <p className="text-gray-600 mb-4">Set your new password:</p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                      placeholder="Enter new password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#3B0069] focus:border-[#3B0069]"
                      placeholder="Confirm new password"
                    />
                  </div>

                  <button
                    onClick={resetPasswordWithOTP}
                    disabled={
                      isLoading ||
                      !passwordData.newPassword ||
                      !passwordData.confirmPassword
                    }
                    className="w-full py-2 bg-[#3B0069] text-white rounded-lg hover:bg-opacity-90 disabled:bg-opacity-70"
                  >
                    {isLoading ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              )}

              {/* Status messages */}
              {message.text && (
                <div
                  className={`mt-4 py-2 px-3 rounded text-sm ${
                    message.type === "success"
                      ? "bg-green-50 text-green-600"
                      : message.type === "info"
                      ? "bg-blue-50 text-blue-600"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {message.text}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountModal;
